interface CacheEntry<T> {
    data: T;
    timestamp: number;
    ttl: number;
}

export const buildKey = (page: string, resource: string): string => {
    return `${page}.${resource}`;
};

export class LoaderCache {
    private cache: Map<string, CacheEntry<any>>;
    private defaultTTL: number;

    constructor(defaultTTL: number = 5 * 60 * 1000) {
        this.cache = new Map();
        this.defaultTTL = defaultTTL;
    }

    get<T>(key: string): T | null {
        const cached = this.cache.get(key) as CacheEntry<T> | undefined;
        if (!cached) return null;

        if (Date.now() - cached.timestamp > cached.ttl) {
            this.cache.delete(key);
            return null;
        }

        return cached.data;
    }

    set<T>(key: string, data: T, ttl: number = this.defaultTTL): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttl,
        });
    }

    invalidate(key: string): boolean {
        return this.cache.delete(key);
    }

    invalidateByPrefix(prefix: string): number {
        let deletedCount = 0;
        for (const key of this.cache.keys()) {
            if (key.startsWith(prefix)) {
                this.cache.delete(key);
                deletedCount++;
            }
        }
        return deletedCount;
    }

    invalidateByPattern(pattern: RegExp): number {
        let deletedCount = 0;
        for (const key of this.cache.keys()) {
            if (pattern.test(key)) {
                this.cache.delete(key);
                deletedCount++;
            }
        }
        return deletedCount;
    }

    clear(): void {
        this.cache.clear();
    }

    has(key: string): boolean {
        const cached = this.cache.get(key);
        if (!cached) return false;

        if (Date.now() - cached.timestamp > cached.ttl) {
            this.cache.delete(key);
            return false;
        }

        return true;
    }

    size(): number {
        return this.cache.size;
    }

    keys(): string[] {
        return Array.from(this.cache.keys());
    }

    getKeysByPrefix(prefix: string): string[] {
        return Array.from(this.cache.keys()).filter(key =>
            key.startsWith(prefix),
        );
    }
}

export const loaderCache = new LoaderCache();
